import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import api from "../../../../store/api";
import { AppThunk } from "../../../../store/app";
import Config from "../../../../config/Config";
import { apiUrls } from "./apiUrls";

interface InitialState {
  isLoading: boolean;
  agGrid: any;
  perPage: any;
  details: any;
  rowData: any;
}

const initialState = {
  isLoading: false,
  agGrid: null,
  perPage: Config.grid.server.gridOptions?.paginationPageSize,
  details: null,
  rowData: null,
} as InitialState;

const careerGroupSlice = createSlice({
  name: "careerGroup",
  initialState,
  reducers: {
    start: state => {
      state.isLoading = true;
    },
    success: state => {
      state.isLoading = false;
    },
    failure: state => {
      state.isLoading = false;
    },
    setRowData: (state, action: PayloadAction<any>) => {
      state.rowData = action?.payload;
    },
    setGrid: (state, action: PayloadAction<any>) => {
      state.agGrid = action?.payload;
    },
    setPerPageSize: (state, action: PayloadAction<any>) => {
      state.perPage = action?.payload;
      if (state.agGrid) {
        state.agGrid.api.paginationSetPageSize(Number(state.perPage));
        state.agGrid.api.setCacheBlockSize(state.perPage);
      }
    },
    setDetails: (state, action: PayloadAction<any>) => {
      state.details = action?.payload;
    },
  },
});

export const {
  start,
  success,
  failure,
  setRowData,
  setGrid,
  setPerPageSize,
  setDetails,
} = careerGroupSlice.actions;

/** For Listing:Start */
export const fetchList =
  (action?: any): AppThunk<any> =>
    async dispatch => {
      try {
        dispatch(start());
        dispatch(setRowData([]));
        const response = await api.get(apiUrls.list, action);
        dispatch(setRowData(response.data.rows));
        dispatch(success());
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure());
        return Promise.reject(error.data);
      }
    };

/** For Listing:End */

export const createRecord =
  (action: any): AppThunk<any> =>
    async dispatch => {
      try {
        dispatch(start());
        const response = await api.post(apiUrls.create, action);
        dispatch(success(response.data));
        dispatch(fetchList());
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure(error.data));
        return Promise.reject(error.data);
      }
    };

export const updateRecord =
  (id: number, action: any): AppThunk<any> =>
    async dispatch => {
      try {
        dispatch(start());
        const response = await api.post(apiUrls.update(id), action);
        dispatch(success(response.data));
        dispatch(fetchList());
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure(error.data));
        return Promise.reject(error.data);
      }
    };

export const deleteRecord =
  (id: number, action: any): AppThunk<any> =>
    async dispatch => {
      try {
        dispatch(start());
        const response = await api.delete(apiUrls.delete(id), action);
        dispatch(success(response.data));
        dispatch(fetchList());
        return Promise.resolve(response.data);
      } catch (error: any) {
        dispatch(failure(error.data));
        return Promise.reject(error.data);
      }
    };

export const changeStatus =
  (id: number, status: number): AppThunk<any> =>
    async dispatch => {
      try {
        const response = await api.post(apiUrls.changeStatus(id), { status });
        dispatch(fetchList());
        return Promise.resolve(response.data);
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    };


export const details =
  (id: number): AppThunk<any> =>
    async dispatch => {
      try {
        const response = await api.get(apiUrls.details(id));
        dispatch(setDetails(response.data.data));
        return Promise.resolve(response.data);
      } catch (error: any) {
        return Promise.reject(error.data);
      }
    };

const careerGroupReducer = careerGroupSlice.reducer;
export default careerGroupReducer;
